import { useEffect, useState } from "react";
import { HandHeart } from "lucide-react";
import api from "@/lib/api";

/**
 * Social-proof badge showing live volunteer signup count.
 * Polls every 60s. Returns null until the count loads to avoid layout flash.
 *
 * Variants:
 *   "pill"   — compact rounded pill, fits inside the hero or above CTAs
 *   "banner" — full-width strip, good above the Volunteer card
 *   "card"   — bold standalone card with a CTA button
 */
export default function VolunteerCountBadge({ variant = "pill", tone = "light", onJoin, className = "" }) {
  const [count, setCount] = useState(null);

  useEffect(() => {
    let cancel = false;
    const fetchCount = async () => {
      try {
        const r = await api.get("/public/volunteer-count");
        if (!cancel) setCount(r.data?.count ?? 0);
      } catch {
        // silent fail — don't show anything broken
      }
    };
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => { cancel = true; clearInterval(t); };
  }, []);

  if (count === null) return null;
  if (count <= 0) return null; // skip until at least 1 signup exists — no awkward "0 signed up"

  const handleClick = (e) => {
    if (onJoin) {
      e.preventDefault();
      onJoin();
    }
  };

  if (variant === "pill") {
    const pillCls = tone === "dark"
      ? "bg-white/10 backdrop-blur border-amber-300/40 text-amber-50 hover:bg-amber-300 hover:text-emerald-950 hover:border-amber-300"
      : "bg-emerald-700/10 backdrop-blur border-emerald-700/30 text-emerald-900 hover:bg-emerald-700 hover:text-white hover:border-emerald-700";
    const countCls = tone === "dark" ? "text-amber-300 group-hover:text-emerald-950" : "text-rose-600 group-hover:text-amber-300";
    return (
      <a
        href="/volunteer"
        onClick={handleClick}
        data-testid="volunteer-count-pill"
        className={`group inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 transition-all ${pillCls} ${className}`}
      >
        <HandHeart className="w-3.5 h-3.5" />
        <span className="font-[Outfit] text-xs font-semibold tabular-nums">
          <span className={countCls}>{count}</span>
          <span className="ml-1.5">{count === 1 ? "volunteer has joined" : "volunteers have joined"} · Become #{count + 1}</span>
        </span>
        <span className="text-xs font-bold group-hover:translate-x-0.5 transition-transform">→</span>
      </a>
    );
  }

  if (variant === "banner") {
    return (
      <div
        data-testid="volunteer-count-banner"
        className={`flex flex-col sm:flex-row items-center justify-between gap-3 rounded-2xl bg-gradient-to-r from-emerald-700 via-emerald-800 to-emerald-700 px-5 py-4 text-white shadow-lg ${className}`}
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-amber-300 flex items-center justify-center shrink-0">
            <HandHeart className="w-5 h-5 text-emerald-950" />
          </div>
          <div>
            <div className="font-[Outfit] text-xs uppercase tracking-[0.18em] text-amber-300 font-bold">Live volunteer count</div>
            <div className="font-[Cormorant_Garamond] text-2xl text-white leading-tight mt-0.5">
              <span className="tabular-nums">{count}</span> {count === 1 ? "person has" : "people have"} stepped up so far
            </div>
          </div>
        </div>
        <a
          href="/volunteer"
          onClick={handleClick}
          data-testid="volunteer-count-banner-cta"
          className="shrink-0 inline-flex items-center gap-2 rounded-full bg-amber-300 hover:bg-amber-400 px-5 py-2.5 text-emerald-950 font-[Outfit] text-sm font-bold transition-colors"
        >
          Become #{count + 1} →
        </a>
      </div>
    );
  }

  // "card" variant
  return (
    <div
      data-testid="volunteer-count-card"
      className={`rounded-2xl bg-white border-2 border-rose-200 p-5 ${className}`}
    >
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
          <HandHeart className="w-6 h-6 text-rose-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-[Outfit] text-[10px] uppercase tracking-[0.18em] text-rose-600 font-bold">Live count</div>
          <div className="font-[Cormorant_Garamond] text-3xl text-emerald-950 leading-none mt-0.5 tabular-nums">{count}</div>
          <div className="font-[Outfit] text-xs text-emerald-900/70 mt-1">
            {count === 1 ? "volunteer signed up" : "volunteers signed up"} so far
          </div>
        </div>
      </div>
      <a
        href="/volunteer"
        onClick={handleClick}
        data-testid="volunteer-count-card-cta"
        className="mt-4 inline-flex items-center justify-center gap-2 w-full rounded-full bg-rose-500 hover:bg-rose-600 text-white py-2.5 font-[Outfit] text-sm font-bold transition-colors"
      >
        Become #{count + 1} →
      </a>
    </div>
  );
}
